import React, {useMemo, useState} from 'react';
import {CURRENCY_OPTIONS, initialCurrency, saveCurrency} from '../constants';
import {PowerDelta, sumDeltas, filterDeltasByHour} from '../powerData';
import {formatTime} from '../../SpaControl/PeakManagement/economyMode';

interface Props {
  deltas: PowerDelta[];
  totalsCentiKwh: Record<string, number>;
  numDays: number;
  tzHours: number;
  peakSettings: Record<string, number> | null;
}

const sumValues = (obj: Record<string, number>) => Object.values(obj).reduce((a, b) => a + b, 0);

const CURRENCY_SYMBOL: Record<string, string> = {
  AUD: '$',
  CAD: '$',
  CZK: 'Kč',
  DKK: 'kr',
  EUR: '€',
  GBP: '£',
  ISK: 'kr',
  NOK: 'kr',
  RUB: 'руб.',
  SEK: 'kr',
  USD: '$',
};

const SUBUNIT_LABEL: Record<string, string> = {
  AUD: '¢',
  CAD: '¢',
  CZK: 'Haléř',
  DKK: 'Øre',
  EUR: '¢',
  GBP: 'p',
  ISK: 'Eyrir',
  NOK: 'kr',
  RUB: 'Kopek',
  SEK: 'kr',
  USD: '¢',
};

const subunitFor = (currency: string) => SUBUNIT_LABEL[currency] ?? '¢';

const formatCurrency = (amount: number, currency: string) => {
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;
  const num = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${symbol} ${num}`;
};

interface PeakWindow {
  mode: number;
  startHour: number;
  endHour: number;
}

// Named and coloured as on the Economy Mode page.
const MODE_LABELS: Record<number, string> = {
  1: 'Off-Peak',
  2: 'Mid-Peak',
  3: 'On-Peak',
};

const MODE_CLASS: Record<number, string> = {
  1: 'off',
  2: 'mid',
  3: 'peak',
};

// Most expensive first, as on the Economy page.
const MODE_ORDER = [3, 2, 1];

const buildWindows = (peakSettings: Record<string, number> | null): PeakWindow[] | null => {
  if (!peakSettings) return null;
  const required = ['Mode1','Mode2','Mode3','Mode4','EndMode1','EndMode2','EndMode3','EndMode4'];
  if (required.some(k => peakSettings[k] === undefined)) return null;
  const toHour = (qh: number) => qh / 4;
  return [
    {mode: peakSettings.Mode1, startHour: toHour(peakSettings.EndMode4), endHour: toHour(peakSettings.EndMode1)},
    {mode: peakSettings.Mode2, startHour: toHour(peakSettings.EndMode1), endHour: toHour(peakSettings.EndMode2)},
    {mode: peakSettings.Mode3, startHour: toHour(peakSettings.EndMode2), endHour: toHour(peakSettings.EndMode3)},
    {mode: peakSettings.Mode4, startHour: toHour(peakSettings.EndMode3), endHour: toHour(peakSettings.EndMode4)},
  ];
};

const formatHour = (h: number) => formatTime(h * 60);

interface GroupedSection {
  mode: number;
  label: string;
  tone: string;
  ranges: {startHour: number; endHour: number}[];
  kwh: number;
}

const CostBreakdown: React.FC<Props> = ({deltas, totalsCentiKwh, numDays, tzHours, peakSettings}) => {
  const [currency, setCurrency] = useState(initialCurrency);
  const [usePowerManagement, setUsePowerManagement] = useState(false);
  const [flatRateCents, setFlatRateCents] = useState<string>('12');
  const [ratesByMode, setRatesByMode] = useState<Record<number, string>>({1: '8', 2: '12', 3: '18'});

  const totalKwh = sumValues(totalsCentiKwh) / 100;
  const safeDays = Math.max(numDays, 1);

  const windows = useMemo(() => buildWindows(peakSettings), [peakSettings]);

  const groupedSections: GroupedSection[] = useMemo(() => {
    if (!windows) return [];
    const byMode: Record<number, GroupedSection> = {};
    windows.forEach(w => {
      if (!byMode[w.mode]) {
        byMode[w.mode] = {
          mode: w.mode,
          label: MODE_LABELS[w.mode] ?? `Mode ${w.mode}`,
          tone: MODE_CLASS[w.mode] ?? 'off',
          ranges: [],
          kwh: 0,
        };
      }
      byMode[w.mode].ranges.push({startHour: w.startHour, endHour: w.endHour});
      const matching = filterDeltasByHour(deltas, w.startHour, w.endHour, tzHours);
      byMode[w.mode].kwh += sumValues(sumDeltas(matching)) / 100;
    });
    return MODE_ORDER.map(m => byMode[m]).filter((g): g is GroupedSection => Boolean(g));
  }, [windows, deltas, tzHours]);

  const flatTotal = totalKwh * (parseFloat(flatRateCents) || 0) / 100;
  const flatAvgPerDay = flatTotal / safeDays;

  const pmTotal = groupedSections.reduce(
    (acc, g) => acc + g.kwh * (parseFloat(ratesByMode[g.mode]) || 0) / 100,
    0,
  );
  const pmAvgPerDay = pmTotal / safeDays;

  const days = `${safeDays} ${safeDays === 1 ? 'day' : 'days'}`;
  const total = usePowerManagement ? pmTotal : flatTotal;
  const average = usePowerManagement ? pmAvgPerDay : flatAvgPerDay;
  const unit = `${subunitFor(currency)}/kWh`;

  return (
    <section className="pwr-cost">
      <div className="pwr-cost-head">
        <span className="pwr-section-title">Estimated Cost</span>
        <label className="pwr-currency">
          <span className="visually-hidden">Currency</span>
          <select className="input" value={currency} onChange={e => { setCurrency(e.target.value); saveCurrency(e.target.value); }} aria-label="Currency">
            {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>

      {/* Pricing: one flat rate, or a rate per tariff period from the spa's schedule */}
      <div className="segmented pwr-pricing" role="group" aria-label="Pricing">
        <button className={!usePowerManagement ? 'active' : ''} aria-pressed={!usePowerManagement} onClick={() => setUsePowerManagement(false)}>
          Flat Rate
        </button>
        <button className={usePowerManagement ? 'active' : ''} aria-pressed={usePowerManagement} onClick={() => setUsePowerManagement(true)} disabled={!windows}>
          Time of Use
        </button>
      </div>

      {!usePowerManagement && (
        <label className="field pwr-rate">
          <span className="field-label">Rate ({unit})</span>
          <input
            className="input"
            type="number"
            min={0}
            step={0.1}
            value={flatRateCents}
            onChange={e => setFlatRateCents(e.target.value)}
          />
        </label>
      )}

      {usePowerManagement && groupedSections.length > 0 && (
        <>
          <div className="pwr-periods">
            {groupedSections.map(g => (
              <div key={g.mode} className={`pwr-period ${g.tone}`}>
                <span className="pwr-period-bar" />
                <div className="pwr-period-body">
                  <span className="pwr-period-label">{g.label}</span>
                  <div className="pwr-period-ranges">
                    {g.ranges.map((r, i) => <span key={i}>{formatHour(r.startHour)} - {formatHour(r.endHour)}</span>)}
                  </div>
                  <span className="pwr-period-kwh">{g.kwh.toFixed(2)} kWh</span>
                </div>
                <label className="field pwr-period-rate">
                  <span className="field-label">{unit}</span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step={0.1}
                    value={ratesByMode[g.mode] ?? ''}
                    onChange={e => setRatesByMode(prev => ({...prev, [g.mode]: e.target.value}))}
                    aria-label={`${g.label} rate (${unit})`}
                  />
                </label>
              </div>
            ))}
          </div>
          <p className="pwr-note">Based on the spa's current Economy Mode schedule.</p>
        </>
      )}

      {usePowerManagement && groupedSections.length === 0 && (
        <p className="pwr-note">Economy Mode schedule is not available for this spa.</p>
      )}

      <div className="pwr-totals">
        <div className="pwr-stat">
          <span className="pwr-stat-label">Total · {days}</span>
          <span className="pwr-stat-value"><span className="pwr-approx" aria-label="approximately">≈</span>{formatCurrency(total, currency)}</span>
        </div>
        <div className="pwr-stat">
          <span className="pwr-stat-label">Average · per day</span>
          <span className="pwr-stat-value"><span className="pwr-approx" aria-label="approximately">≈</span>{formatCurrency(average, currency)}</span>
        </div>
      </div>
    </section>
  );
};

export default CostBreakdown;
